import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import moment from "moment";

import { GlobalInfo } from "helpers/AppProvider.types";
import { setGlobalInfoSnapshot } from "helpers/globalContext/globalInfoSnapshot";

import { APIHandler } from "utilities/apiHandler";
import { getCurrentEnvironment } from "utilities/environment/environment";

export interface IAppProviderHandler {
    get(encryptedBuilderId?: string): Promise<GlobalInfo>;
    isLegacy: boolean;
    /**
     * Discriminates this handler's global info from every other handler's in the query cache.
     * Handlers resolving the same global info share a value — every `AppProviderHandler` instance
     * is `"api"`, so the per-layout instances still share one cache entry. A handler whose result
     * depends on an argument must fold that argument in, or two instances collide on one entry.
     */
    cacheKey: string;
}

export class AppProviderHandler implements IAppProviderHandler {
    isLegacy = false;
    cacheKey = "api";

    async get(encryptedBuilderId?: string): Promise<GlobalInfo> {
        const globalInfo = await APIHandler(`/api/AccountInfo/GlobalInfo`, {
            method: "GET",
            data: { encryptedBuilderId },
            responseType: GlobalInfo,
        });
        // Deprecate btJScriptGlobals for SPA pages
        if (window.btJScriptGlobals && !window.btJScriptGlobals.spaProxyBypass) {
            const btJScriptGlobalsOrig = window.btJScriptGlobals;
            const btJScriptGlobalsProxy: ProxyHandler<object> = {
                get: (target, prop) => {
                    // DO NOT USE THIS PROPERTY WITHOUT TALKING TO ARCH
                    if (prop === "spaProxyBypass") {
                        return target;
                    } else if (getCurrentEnvironment() !== "prod") {
                        console.error(
                            `An attempt was made to access btJScriptGlobals from a SPA enabled page [key="${String(
                                prop,
                            )}"]. Refactor to pull from context or an API endpoint instead.`,
                        );
                    }
                    return target[prop as keyof typeof target];
                },
            };

            // Add the spa proxy bypass property
            // DO NOT USE THIS PROPERTY WITHOUT TALKING TO ARCH
            btJScriptGlobalsOrig.spaProxyBypass = btJScriptGlobalsOrig;

            const proxy = new Proxy(btJScriptGlobalsOrig, btJScriptGlobalsProxy);
            window.btJScriptGlobals = proxy;
        }

        return globalInfo;
    }
}

export const defaultAppProviderHandler = new AppProviderHandler();

// Hooks

/** Queries */

export type BTQueryOptions<
    TQueryFnData = unknown,
    TError = unknown,
    TQueryKey extends unknown[] = [],
    TData = TQueryFnData,
> = Partial<
    Pick<
        UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
        | "onSuccess"
        | "onError"
        | "onSettled"
        | "initialData"
        | "staleTime"
        | "cacheTime"
        | "retry"
        | "retryDelay"
        | "enabled"
        | "refetchOnWindowFocus"
    >
>;

export const globalInfoQueryKey = "globalInfo";
export const useGetGlobalInfoQuery = (
    handler: IAppProviderHandler,
    options?: BTQueryOptions<GlobalInfo, Error, string[]>,
    encryptedBuilderId?: string,
) => {
    return useQuery(
        [globalInfoQueryKey, handler.cacheKey],
        async () => {
            const globalInfo = await handler.get(encryptedBuilderId);
            // Source is passed through because `AppProvider` defaults to the btJScriptGlobals
            // handler, so a nested provider would otherwise republish the inlined values over an
            // already-resolved API payload.
            setGlobalInfoSnapshot(globalInfo, handler.isLegacy ? "btJScriptGlobals" : "api");
            return globalInfo;
        },
        {
            retry: 3,
            cacheTime: moment.duration(1, "day").asMilliseconds(),
            staleTime: moment.duration(1, "day").asMilliseconds(),
            ...options,
        },
    );
};
